import { useCallback, useContext, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import StaffingModal from './StaffingModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApiClient } from '@/context/ApiContext';
import { CurrentUserContext } from '@/context/CurrentUserContext';
import {
    getBlockedRelationships,
    createBlockedRelationship,
    deleteBlockedRelationship,
} from '@/services/staffing/Staffing';
import { BlockedRelationship } from '@/types/staffing/blockedRelationship';
import { PBSPeoplePicker } from '@/components/helper/PBSPeoplePicker';
import { StaffingClientPicker } from '@/components/helper/StaffingClientPicker';

interface BlockedRelationshipsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Provide when opened from a client context menu */
    clientId?: number;
    clientName?: string | null;
    /** Provide when opened from a staff context menu */
    staff?: string;
    staffName?: string | null;
}

const DOMAIN = 'positive';

const BlockedRelationshipsModal = ({
    open,
    onOpenChange,
    clientId,
    clientName,
    staff,
    staffName,
}: BlockedRelationshipsModalProps) => {
    const apiClient = useApiClient();
    const { accessControl } = useContext(CurrentUserContext);
    const canWrite = accessControl.canWrite('staffing_dashboard');

    const isClientContext = clientId !== undefined;
    const title = isClientContext
        ? `Blocked Relationships — ${clientName ?? clientId}`
        : `Blocked Relationships — ${staffName ?? staff}`;

    const [items, setItems] = useState<BlockedRelationship[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // new-item form state
    const [selectedStaffLogins, setSelectedStaffLogins] = useState<string[]>([]);
    const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const fetchItems = useCallback(() => {
        setLoading(true);
        setError(null);
        const staffWithDomain = staff
            ? staff.includes('\\') ? staff : `${DOMAIN}\\${staff}`
            : '';
        const filter = isClientContext
            ? `ClientId eq ${clientId}`
            : `Staff eq '${staffWithDomain}'`;
        const expand = isClientContext ? 'StaffMember' : 'Client';
        getBlockedRelationships({ filter, expand: [expand] }, apiClient)
            .then((res) => setItems(res.data.value ?? res.data))
            .catch(() => setError('Failed to load blocked relationships.'))
            .finally(() => setLoading(false));
    }, [apiClient, clientId, staff, isClientContext]);

    useEffect(() => {
        if (open) {
            fetchItems();
            setSelectedStaffLogins([]);
            setSelectedClientIds([]);
            setReason('');
            setSubmitError(null);
        }
    }, [open, fetchItems]);

    const canSubmit =
        reason.trim() !== '' &&
        (isClientContext ? selectedStaffLogins.length > 0 : selectedClientIds.length > 0);

    const handleCreate = () => {
        if (!canSubmit) return;

        const requests = isClientContext
            ? selectedStaffLogins.map((login) => ({
                  clientId: clientId!,
                  staff: `${DOMAIN}\\${login}`,
                  reason: reason.trim(),
              }))
            : selectedClientIds.map((cid) => ({
                  clientId: cid,
                  staff: `${DOMAIN}\\${staff}`!,
                  reason: reason.trim(),
              }));

        setSubmitting(true);
        setSubmitError(null);
        Promise.all(requests.map((req) => createBlockedRelationship(req, apiClient)))
            .then(() => {
                setSelectedStaffLogins([]);
                setSelectedClientIds([]);
                setReason('');
                fetchItems();
            })
            .catch((err) => {
                const code = err?.response?.data?.extensions?.code;
                if (code === 'BlockedRelationship.Conflict.DuplicatePair') {
                    setSubmitError('One or more of these blocked relationships already exists.');
                } else {
                    setSubmitError('Failed to create one or more blocked relationships.');
                }
                fetchItems();
            })
            .finally(() => setSubmitting(false));
    };

    const handleDelete = (id: number) => {
        setError(null);
        deleteBlockedRelationship(id, apiClient)
            .then(() => fetchItems())
            .catch(() => setError('Failed to delete blocked relationship.'));
    };

    const displayName = (item: BlockedRelationship) =>
        isClientContext
            ? (item.StaffMember?.FullName ?? item.Staff)
            : (item.Client?.Name ?? String(item.ClientId));

    return (
        <StaffingModal open={open} onOpenChange={onOpenChange} title={title} wide>
            <div className="flex flex-col gap-4 p-1">
                {canWrite && (
                    <div className="flex flex-col gap-3 border rounded-md p-3 bg-muted/30">
                        {isClientContext ? (
                            <PBSPeoplePicker
                                label="Staff Member"
                                required
                                onChange={(logins) =>
                                    setSelectedStaffLogins(logins ? logins.split(',').filter(Boolean) : [])
                                }
                            />
                        ) : (
                            <StaffingClientPicker
                                label="Client"
                                required
                                onChange={(ids) => setSelectedClientIds(ids)}
                            />
                        )}
                        <div className="flex flex-col gap-1">
                            <Label>
                                Reason <span className="text-destructive">*</span>
                            </Label>
                            <Input
                                placeholder="Reason for blocking..."
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                        {submitError && <p className="text-sm text-destructive">{submitError}</p>}
                        <div className="flex justify-end">
                            <Button size="sm" onClick={handleCreate} disabled={submitting || !canSubmit}>
                                {submitting ? 'Adding...' : 'Add Block'}
                            </Button>
                        </div>
                    </div>
                )}

                {error && <p className="text-sm text-destructive">{error}</p>}

                {loading ? (
                    <p className="text-sm text-muted-foreground">Loading...</p>
                ) : items.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No blocked relationships.</p>
                ) : (
                    <div className="flex flex-col gap-2">
                        {items.map((item) => (
                            <div
                                key={item.Id}
                                className="border rounded-md p-3 flex items-start justify-between gap-3"
                            >
                                <div className="flex flex-col gap-0.5 min-w-0">
                                    <span className="text-sm font-medium truncate">{displayName(item)}</span>
                                    {item.Reason && (
                                        <span className="text-xs text-muted-foreground">{item.Reason}</span>
                                    )}
                                    <span className="text-xs text-muted-foreground">
                                        Added by {item.CreatedBy}
                                    </span>
                                </div>
                                {canWrite && (
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                                        title="Remove block"
                                        onClick={() => handleDelete(item.Id)}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </StaffingModal>
    );
};

export default BlockedRelationshipsModal;
